import { LogOut, Monitor, Moon, Settings, Shield, Sun, UserCog } from 'lucide-react';

import { ThemeMode } from '@shigo-ui/theme';
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  motionClasses,
} from '@shigo-ui/ui';

export function DashboardSettingsMenu({
  themeMode,
  onThemeChange,
  onAdminPanelClick,
  onAccountSettingsClick,
  onSignOut,
}: {
  themeMode: ThemeMode;
  onThemeChange: ((themeMode: ThemeMode) => void) | undefined;
  onAdminPanelClick: (() => void) | undefined;
  onAccountSettingsClick: (() => void) | undefined;
  onSignOut: (() => void) | undefined;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          aria-label="Settings"
          className={cn(
            'text-muted-foreground hover:bg-secondary hover:text-foreground relative rounded-md p-2',
            motionClasses.subtleControl,
          )}
        >
          <Settings className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuItem
          className="cursor-pointer gap-2"
          {...(onAdminPanelClick
            ? {
                onSelect: () => {
                  onAdminPanelClick();
                },
              }
            : {})}
        >
          <Shield className="h-4 w-4" />
          <span>Admin panel</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer gap-2"
          {...(onAccountSettingsClick
            ? {
                onSelect: () => {
                  onAccountSettingsClick();
                },
              }
            : {})}
        >
          <UserCog className="h-4 w-4" />
          <span>Account Settings</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-muted-foreground text-[11px] font-semibold tracking-[0.08em] uppercase">
          Appearance
        </DropdownMenuLabel>
        <div className="px-2 pb-1.5">
          <div className="bg-background inline-flex w-full gap-0.5 rounded-md p-0.5">
            {themeOptions.map(({ icon: Icon, label, value }) => {
              const selected = themeMode === value;

              return (
                <button
                  key={value}
                  type="button"
                  aria-label={`${label} theme`}
                  aria-pressed={selected}
                  title={`${label} theme`}
                  className={cn(
                    'flex h-7 flex-1 cursor-pointer items-center justify-center rounded-[5px] transition-colors',
                    selected
                      ? 'bg-card text-foreground shadow-[var(--shadow-card)]'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                  onClick={() => {
                    onThemeChange?.(value);
                  }}
                >
                  <Icon className="h-4 w-4" />
                </button>
              );
            })}
          </div>
        </div>

        {onSignOut ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive cursor-pointer gap-2"
              onSelect={() => {
                onSignOut();
              }}
            >
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const themeOptions = [
  { value: ThemeMode.Light, label: 'Light', icon: Sun },
  { value: ThemeMode.System, label: 'System', icon: Monitor },
  { value: ThemeMode.Dark, label: 'Dark', icon: Moon },
] as const;
